import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import ReactPixel from "react-facebook-pixel";

function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [message, setMessage] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const subDomain = window.location.hostname.split(".")[0];
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const apiUrl = process.env.REACT_APP_API_URL;

  // List of predefined subjects
  const subjects = [
    "More Info Request",
    "Support",
    "Billing Inquiry",
    "Partnership Opportunities",
    "Technical Issues",
    "Feature Request",
    "Cancellation Request",
    "Account Access Issue",
    "Other",
  ];
  
  const formatPhoneNumber = (input) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, "");
    // Apply formatting (123) 456-7890
    if (cleaned.length <= 3) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // 📌 Handle phone number input change
  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  // 📌 Validate phone number format before submission
  const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, ""); // Remove non-numeric chars
    return cleaned.length === 10; // Ensure it's exactly 10 digits
  };

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset messages
    setError("");
    setSuccessMessage("");

    // Validate reCAPTCHA
    if (!captchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    // Validate phone number
    if (!isValidPhoneNumber(phone)) {
      setError("Please enter a valid phone number in the format: (123) 456-7890");
      return;
    }

    ReactPixel.track("Contact");
    //console.log("Contact Form Submitted - Tracked");

    setLoading(true);

    // Determine the final subject
    const finalSubject = subject === "Other" ? customSubject : subject;

    // Prepare payload
    const payload = {
      name,
      email,
      phone,
      subject: finalSubject,
      message,
      recaptchaToken: captchaValue,
      subdomain: subDomain,
    };

    try {
      // Send form data to backend
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "handleContactFormSubmission",
          payload: payload,
        }),
      });

      const data = await response.json(); // Parse response JSON

      if (!data.success) {
        throw new Error(data.message || "Failed to submit the form.");
      }

      // Success message
      setSuccessMessage("Your message has been sent successfully! We will be in touch shortly.");

      // Clear form after submission
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setCustomSubject("");
      setMessage("");
      setCaptchaValue(null);
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="contact" className="bg-gray-800 text-gray-100 py-16">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold mb-8 text-center">Contact Us</h2>

        {successMessage && (
          <div className="mb-6 text-center text-green-400 bg-green-900/50 p-3 rounded-lg">
            {successMessage}
          </div>
        )}

        <form className="max-w-lg mx-auto" onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
              required
            />
          </div>

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="block mb-2">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
              placeholder="(123) 456-7890"
              required
            />
          </div>

          {/* Subject Dropdown */}
          <div className="mb-4">
            <label className="block mb-2">Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
              required
            >
              <option value="" disabled>Select a subject</option>
              {subjects.map((subj) => (
                <option key={subj} value={subj}>{subj}</option>
              ))}
            </select>
          </div>

          {/* Custom Subject Input (only if "Other" is selected) */}
          {subject === "Other" && (
            <div className="mb-4">
              <label className="block mb-2">Other Subject</label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
                placeholder="Enter your subject"
                required
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
              rows="4"
              required
            ></textarea>
          </div>

          {/* reCAPTCHA v2 Checkbox */}
          <div className="mb-4 flex justify-center">
            <ReCAPTCHA sitekey={recaptchaKey} onChange={handleCaptchaChange} />
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-red-400 text-center mb-4 bg-red-900/50 p-3 rounded-lg">
              {error}
            </p>
          )}

          {/* Submit Button with Spinner */}
          <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full flex items-center justify-center" disabled={!captchaValue || loading}>
          {loading ? (
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;



// import React, { useState } from "react";
// import ReCAPTCHA from "react-google-recaptcha";

// function ContactForm() {
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [subject, setSubject] = useState("");
//   const [customSubject, setCustomSubject] = useState("");
//   const [message, setMessage] = useState("");
//   const [captchaValue, setCaptchaValue] = useState(null);
//   const [error, setError] = useState("");
//   const [successMessage, setSuccessMessage] = useState("");
//   const [loading, setLoading] = useState(false);

//   const subDomain = window.location.hostname.split(".")[0];
//   console.log(subDomain);
//   const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
//   const apiUrl = process.env.REACT_APP_API_URL;

//   // List of predefined subjects
//   const subjects = [
//     "More Info Request",
//     "Support",
//     "Billing Inquiry",
//     "Partnership Opportunities",
//     "Technical Issues",
//     "Feature Request",
//     "Cancellation Request",
//     "Account Access Issue",
//     "Other",
//   ];

//   // Handle reCAPTCHA verification
//   const handleCaptchaChange = (value) => {
//     setCaptchaValue(value);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Reset success message and errors
//     setError("");
//     setSuccessMessage("");

//     // Validate reCAPTCHA
//     if (!captchaValue) {
//       setError("Please complete the reCAPTCHA verification.");
//       return;
//     }

//     setLoading(true);

//     // Determine the final subject
//     const finalSubject = subject === "Other" ? customSubject : subject;

//     // Prepare payload
//     const payload = {
//       name,
//       email,
//       subject: finalSubject,
//       message,
//       recaptchaToken: captchaValue,
//       subdomain: subDomain,
//     };

//     try {
//       // Send form data to backend
//       const response = await fetch(`${apiUrl}/dataServer`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           action: "handleContactFormSubmission",
//           payload: payload,
//         }),
//       });

//       const data = await response.json(); // Parse response JSON

//       if (!data.success) {
//         throw new Error(data.message || "Failed to submit the form.");
//       }

//       // Success message
//       setSuccessMessage("✅ Your message has been sent successfully! We will be in touch shortly.");

//       // Clear form after submission
//       setName("");
//       setEmail("");
//       setSubject("");
//       setCustomSubject("");
//       setMessage("");
//       setCaptchaValue(null);
//     } catch (error) {
//       console.error("Error:", error);
//       setError("An error occurred. Please try again later.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div id="contact" className="bg-gray-800 text-gray-100 py-16">
//       <div className="container mx-auto px-6">
//         <h2 className="text-3xl font-bold mb-8 text-center">Contact Us</h2>
        
//         {successMessage && (
//           <div className="mb-6 text-center text-green-400 bg-green-900/50 p-3 rounded-lg">
//             {successMessage}
//           </div>
//         )}

//         <form className="max-w-lg mx-auto" onSubmit={handleSubmit}>
//           <div className="mb-4">
//             <label className="block mb-2">Name</label>
//             <input
//               type="text"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
//               required
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block mb-2">Email</label>
//             <input
//               type="email"
//               value={email}
//               onChange={(e) => setEmail(e.target.value)}
//               className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
//               required
//             />
//           </div>

//           {/* Subject Dropdown */}
//           <div className="mb-4">
//             <label className="block mb-2">Subject</label>
//             <select
//               value={subject}
//               onChange={(e) => setSubject(e.target.value)}
//               className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
//               required
//             >
//               <option value="" disabled>Select a subject</option>
//               {subjects.map((subj) => (
//                 <option key={subj} value={subj}>{subj}</option>
//               ))}
//             </select>
//           </div>

//           {/* Custom Subject Input (only if "Other" is selected) */}
//           {subject === "Other" && (
//             <div className="mb-4">
//               <label className="block mb-2">Other Subject</label>
//               <input
//                 type="text"
//                 value={customSubject}
//                 onChange={(e) => setCustomSubject(e.target.value)}
//                 className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
//                 placeholder="Enter your subject"
//                 required
//               />
//             </div>
//           )}

//           <div className="mb-4">
//             <label className="block mb-2">Message</label>
//             <textarea
//               value={message}
//               onChange={(e) => setMessage(e.target.value)}
//               className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
//               rows="4"
//               required
//             ></textarea>
//           </div>

//           {/* reCAPTCHA v2 Checkbox */}
//           <div className="mb-4 flex justify-center">
//             <ReCAPTCHA
//               sitekey={recaptchaKey}
//               onChange={handleCaptchaChange}
//             />
//           </div>

//           {/* Error Message */}
//           {error && (
//             <p className="text-red-400 text-center mb-4 bg-red-900/50 p-3 rounded-lg">
//               {error}
//             </p>
//           )}

//           {/* Submit Button with Spinner */}
//           <button
//             type="submit"
//             className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full flex items-center justify-center"
//             disabled={!captchaValue || loading}
//           >
            // {loading ? (
            //   <svg
            //     className="animate-spin h-5 w-5 mr-2 text-white"
            //     viewBox="0 0 24 24"
            //     fill="none"
            //     xmlns="http://www.w3.org/2000/svg"
            //   >
            //     <circle
            //       className="opacity-25"
            //       cx="12"
            //       cy="12"
            //       r="10"
            //       stroke="currentColor"
            //       strokeWidth="4"
            //     />
            //     <path
            //       className="opacity-75"
            //       fill="currentColor"
            //       d="M4 12a8 8 0 018-8v8H4z"
            //     />
            //   </svg>
            // ) : (
            //   "Submit"
            // )}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }

// export default ContactForm;